import { createFont } from "@tamagui/core";
import { createInterFont } from "@tamagui/font-inter";

const interFont = createInterFont();

// Native only (web ignores `face` and uses the CSS family string). Maps the
// weights the app asks for onto the expo-font names registered in
// mobile/app/theme/index.ts — without this every <Text> requested family
// "Inter", which is not a registered name, and fell back to the system font.
const interFace = {
  400: { normal: "InterRegular", italic: "InterRegularItalic" },
  600: { normal: "InterSemiBold", italic: "InterSemiBoldItalic" },
  700: { normal: "InterBold", italic: "InterBoldItalic" },
  800: { normal: "InterExtraBold", italic: "InterExtraBoldItalic" },
  900: { normal: "InterBlack", italic: "InterBlackItalic" },
};

// ONE key -> px table for every font. `$8` is 24px whether the element is
// body or heading; keys rise with the pixels. Renumbered 2026-09-21 (rank 9,
// step 7) from a table where `$6` was 30px but `$7` was 22px, and where `$8`
// meant 26px in body and 48px in heading. Every call site was remapped
// pixel-for-pixel by scratchpad/renumber_fonts.js; the half keys (9.5, 13.5)
// exist only because the old heading scale had 28px and 40px steps.
const size = {
  1: 12,
  2: 13,
  3: 14,
  4: 16,
  5: 18,
  6: 20,
  7: 22,
  8: 24,
  9: 26,
  9.5: 28,
  10: 30,
  11: 32,
  12: 36,
  13: 38,
  13.5: 40,
  14: 48,
  15: 56,
  16: 60,
  17: 66,
};

export const fonts = {
  body: createFont({
    ...interFont,
    face: interFace,
    size,
    letterSpacing: {},
    // Values are what each pixel size resolved to before the renumber
    // (createFont used to fill missing keys forward), so `size="$N"` renders
    // exactly as it did. Keys 14-17 never had a body consumer; sane ratios.
    lineHeight: {
      1: 14,
      2: 15,
      3: 17,
      4: 20,
      5: 24,
      6: 24,
      7: 29,
      8: 30,
      9: 33,
      9.5: 34,
      10: 36,
      11: 39,
      12: 39,
      13: 46,
      13.5: 48,
      14: 58,
      15: 67,
      16: 72,
      17: 79,
    },
    weight: {
      1: "400",
      2: "400",
      3: "400",
      4: "400",
      5: "700",
      6: "700",
      7: "800",
      8: "800",
      9: "800",
      9.5: "800",
      10: "800",
      11: "800",
      12: "800",
      13: "800",
      13.5: "800",
      14: "800",
      15: "800",
      16: "900",
      17: "900",
    },
  }),

  heading: createFont({
    ...interFont,
    face: interFace,
    size,
    letterSpacing: {},
    // 1.5x the size, as before; 36px keeps the 48 it always rendered with.
    lineHeight: {
      1: 18,
      2: 20,
      3: 21,
      4: 24,
      5: 27,
      6: 30,
      7: 33,
      8: 36,
      9: 39,
      9.5: 42,
      10: 45,
      11: 48,
      12: 48,
      13: 57,
      13.5: 60,
      14: 72,
      15: 84,
      16: 90,
      17: 99,
    },
    // Headings up to 28px render uppercase (Tamagui's H6 default lands
    // there); 30px and above do not. Applies through the `size` prop only.
    transform: {
      1: "uppercase",
      2: "uppercase",
      3: "uppercase",
      4: "uppercase",
      5: "uppercase",
      6: "uppercase",
      7: "uppercase",
      8: "uppercase",
      9: "uppercase",
      9.5: "uppercase",
      10: "none",
      11: "none",
      12: "none",
      13: "none",
      13.5: "none",
      14: "none",
      15: "none",
      16: "none",
      17: "none",
    },
    weight: {
      1: "400",
      2: "400",
      3: "400",
      4: "400",
      5: "400",
      6: "400",
      7: "400",
      8: "400",
      9: "700",
      9.5: "700",
      10: "700",
      11: "800",
      12: "800",
      13: "800",
      13.5: "800",
      14: "800",
      15: "800",
      16: "900",
      17: "900",
    },
  }),
};
